import React, { useEffect, useState } from 'react';
import './projectDetails.css';
import { IoIosArrowBack } from 'react-icons/io';
import EmployeeDetails from './EmployeeDetails/EmployeeDetails';
import CurrentlyWorking from './currentlyWorking/currentlyWorking';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../../utils/axiosInstance';

const ProjectDetails = ({ project, onBack, loggedInEmployeeId }) => {
  const { t } = useTranslation();
  const [employeeWorkData, setEmployeeWorkData] = useState([]);
  const employeeColors = ['#ff6b6b', '#48dbfb', '#1dd1a1', '#feca57', '#5f27cd'];

  useEffect(() => {
    if (!project) return;
    const fetchData = async () => {
      try {
        const assignmentsResponse = await axiosInstance.get('/admin/empProjRelation');
        const assignments = await assignmentsResponse.data;
        const projectAssignIds = assignments
          .filter(assignment => assignment.project_id === project.id)
          .map(assignment => assignment.assign_id);

        const empProjWorkResponse = await axiosInstance.get('/admin/empProjWork');
        const data = await empProjWorkResponse.data;
        const filteredLogs = data.filter(log => projectAssignIds.includes(log.assign_id));

        const processedData = project.employees.map(employee => {
          const employeeLogs = filteredLogs.filter(log => log.p_name === employee.name);

          const todayLogs = employeeLogs.filter(log => new Date(log.work_date).toDateString() === new Date().toDateString());

          const totalDuration = calculateTotalDuration(employeeLogs);
          const todayDuration = calculateTotalDuration(todayLogs);
          const totalOvertime = calculateOvertimeDuration(employeeLogs);

          return [
            // employee.id.toString(),
            employee.sap_id,
            employee.name,
            formatDurationHHMMSS(todayDuration),
            formatDurationHHMMSS(totalOvertime),
            formatDurationHHMMSS(totalDuration)
          ];
        });

        setEmployeeWorkData(processedData);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [project]);

  const calculateTotalDuration = (logs) => {
    return logs.reduce((total, log) => {
      if (!log || !log.work_duration) return total;
      const [hours, minutes, seconds] = log.work_duration.split(':').map(Number);
      return total + (hours * 3600) + (minutes * 60) + seconds;
    }, 0);
  };

  const calculateOvertimeDuration = (logs) => {
    return logs.reduce((total, log) => {
      if (log.overtime === "Overtime" && log.work_duration) {
        const [hours, minutes, seconds] = log.work_duration.split(':').map(Number);
        return total + (hours * 3600) + (minutes * 60) + seconds;
      }
      return total;
    }, 0);
  };

  const formatDurationHHMMSS = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    const options = { day: '2-digit', month: 'long', year: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleExportClick = () => {
    exportToExcel(project, employeeWorkData);
  };

  const exportToExcel = async (project, employeeWorkData) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Project Details');

    // Set up worksheet
    worksheet.views = [{ showGridLines: false }];
    worksheet.addRow([]);

    // Add title
    const titleRow = worksheet.addRow(['', t("projectDetails_title")]);
    titleRow.height = 30; // Increase row height for better visibility
    worksheet.mergeCells('B2:G2');
    const titleCell = titleRow.getCell(4);
    titleCell.font = { size: 18, bold: true };
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '9BC2E6' } };
    titleCell.alignment = { vertical: 'middle', horizontal: 'center' };

    worksheet.addRow([]);

    // Add project details
    const addOutlinedRow = (label, value) => {
      const row = worksheet.addRow(['', label, value]);
      row.getCell(2).font = { bold: true };
    };
    addOutlinedRow(t("addproject_name"), project.name);
    addOutlinedRow(t("addProject_desc"), project.description);
    worksheet.addRow([]);

    const dateHeaderRow = worksheet.addRow(['', t("projectDetails_excelStrtDate"), t("projectDetails_excelEndDate"), t("projectDetails_excelDownloadedon")]);
    dateHeaderRow.getCell(2).font = { bold: true };
    dateHeaderRow.getCell(3).font = { bold: true };
    dateHeaderRow.getCell(4).font = { bold: true };
    const dateValueRow = worksheet.addRow(['', formatDate(project.startDate), formatDate(project.endDate), new Date().toLocaleDateString()]);
    worksheet.addRow([]);

    // Add headers
    const headers = ['', t("employeeWorkDetails_SrNo"), t("projectDetails_EmployeeID"), t("empForm_namePlace"), t("projectDetails_todayWork"), t("projectDetails_totalOvertime"), t("projectDetails_totalWorking")];

    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell, colNumber) => {
      if (colNumber !== 1) { // Skip the first (blank) cell
        cell.font = { bold: true };
        cell.alignment = { horizontal: 'center' };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'C5D9F1' } };
      }
    });

    // Add employee data
    if (Array.isArray(employeeWorkData)) {
      employeeWorkData.forEach((row, index) => {
        const newRow = worksheet.addRow(['', index + 1, ...row]);
        newRow.eachCell((cell) => {
          cell.alignment = { horizontal: 'center' };
        });
      });
    }

    // Set column widths
    worksheet.columns.forEach((column) => {
      column.width = 20;
    });

    // Generate and save file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `project_details_of_${project.name}.xlsx`);
    toast.success("Download started.");
  };

  return (
    <div className="projectDetails">
      <button className="backButton" onClick={onBack}>
        <IoIosArrowBack style={{ marginRight: '5px' }} /> {t("projectDetails_back")}
      </button>
      <div className="projectHeader">
        <div className='PageHeader'>
          <h2>{project.name}</h2>
          <p className='descr'>{project.description}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <button className="downloadButton" onClick={handleExportClick}>{t("projectDetails_download")}</button>
        </div>
      </div>
      <div className='empDates'>
        <div className="leftSection">
          <p className='workTxt'>{t("projectDetails_employeesWorking")}</p>
          <div className="employeeInitialsCard">
            <div className="employeeInitialsList">
              {project.employees.map((employee, index) => (
                <div
                  className="employeeInitialCircle"
                  key={employee.id}
                  style={{ borderColor: employeeColors[index % employeeColors.length] }}
                >
                  {employee.name.split(' ').map(name => name[0].toUpperCase()).join('')}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="rightSection">
          <div className="projectDates">
            <p>{t("projectDetails_startDate")}{formatDate(project.startDate)}</p>
            <p>{t("projectDetails_endDate")}{formatDate(project.endDate)}</p>
          </div>
        </div>
      </div>
      <div className="content">
        <div className='empDetails'><EmployeeDetails project={project} /></div>
        <div className='currentEmps'><CurrentlyWorking project={project} employeeId={loggedInEmployeeId} /></div>
      </div>
    </div>
  );
};

export default ProjectDetails;
